/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Component voor het knoppenblok rechtsonder in een canvas (tot golf 4f een Custom Element): de
 * zoom-control plus de knoppen die de aanroepende context erbij vraagt (snapnorm in de modelview,
 * nicestates en snapstates in de simview). Vervangt de `dlpanzoom`-directive en de losse knop-markup
 * in canvas.html; de acties komen als outputs terug naar het bouwscherm, dat ze uitvoert.
 *
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 22-07-2026
 */
import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output, computed, inject, signal} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {BaseIslandComponent} from '../base-island.component';
import {BeginneruitlegDirective} from '../../directives/beginnersuitleg/beginnersuitleg.directive';
import {DlPanzoomComponent} from '../../controls/dl-panzoom/dl-panzoom.component';
import type {CytoCanvasView} from '../../app/canvas/canvasregistratie.service';
import {VideoStatusService} from '../../services/video-status.service';
import {WeergaveInstellingenService} from '../../services/weergave-instellingen.service';

@Component({
    selector: 'dl-canvasknoppen',
    standalone: true,
    templateUrl: './dl-canvasknoppen.component.html',
    styleUrls: ['./dl-canvasknoppen.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, BeginneruitlegDirective, DlPanzoomComponent],
    host: {
        '[class.dl-canvasknoppen--zichtbaar]': 'zichtbaar()'
    }
})
export class DlCanvasknoppenComponent extends BaseIslandComponent {

    /**
     * De gedeelde videostatus: het modelblok wijkt voor een spelende instructievideo.
     */
    private readonly videoStatus = inject(VideoStatusService);

    /**
     * De weergave-instellingen: bij beginnersmodus vervangen de uitlegballonnen de browser-titels.
     */
    protected readonly weergaveInstellingen = inject(WeergaveInstellingenService);

    constructor() {
        super();
    }

    // --- Inputs ---

    /**
     * Het canvas waar dit blok bij hoort: waar de zoomcontrol aan gekoppeld wordt, en of het blok
     * moet wijken voor de instructievideo.
     */
    @Input()
    set view(waarde: CytoCanvasView) {
        this._view.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _view = signal<CytoCanvasView>('model');

    /**
     * Het laagste zoomniveau dat de zoomcontrol instelt; zonder waarde geldt de default van
     * `DlPanzoomComponent`. (Tot golf 4f kwam dit als attribuut van een custom element als tekst binnen.)
     */
    @Input()
    set minZoom(waarde: number) {
        this._minZoom.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _minZoom = signal(0.1);

    /** Het hoogste zoomniveau dat de zoomcontrol instelt. */
    @Input()
    set maxZoom(waarde: number) {
        this._maxZoom.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _maxZoom = signal(10);

    /**
     * Of de snapnorm-knop getoond wordt. De afweging (normmodel aanwezig, niet aan het simuleren)
     * hoort bij de aanroepende context.
     */
    @Input()
    set toonSnapnorm(waarde: boolean) {
        this._toonSnapnorm.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _toonSnapnorm = signal(false);

    /** Of de knop wordt getoond die de toestanden netjes over het canvas verdeelt. */
    @Input()
    set toonNicestates(waarde: boolean) {
        this._toonNicestates.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _toonNicestates = signal(false);

    /** Of de knop wordt getoond die de toestanden op een raster uitlijnt. */
    @Input()
    set toonSnapstates(waarde: boolean) {
        this._toonSnapstates.set(waarde);
        this.cdr.markForCheck();
    }
    protected readonly _toonSnapstates = signal(false);

    // --- Outputs ---

    /** De gebruiker wil het model op het normmodel laten aansluiten. */
    @Output() readonly snapnorm = new EventEmitter<void>();

    /** De gebruiker wil de toestanden netjes verdelen over het simcanvas. */
    @Output() readonly niceStates = new EventEmitter<void>();

    /** De gebruiker wil de toestanden op een raster uitlijnen. */
    @Output() readonly snapStates = new EventEmitter<void>();

    // --- Afgeleide state ---

    /** Het blok wijkt in de modelview voor een spelende instructievideo. */
    protected readonly zichtbaar = computed(() => this._view() !== 'model' || !this.videoStatus.speelt());
}
